import {
  GatewayAvancement,
  GatewayGarantieFinanciere,
} from "@conformite/gateway";
import {
  Form,
  InputButtonGroup,
  InputCheckbox,
  InputNumber,
  useFormConfig,
  FormField,
  FormGrid,
  FormRow,
  displayErrorToast,
  InfoCard,
} from "@endya-conformite/ui-kit";
import { GatewayGarantieFinanciereApi } from "@src/api/conformite/garantieFinanciere.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useOnce } from "@src/hooks/useOnce";
import { useMe } from "@src/store/store.me";
import { chain } from "lodash";
import { useEffect } from "react";
import styles from "../ConformiteGarantieFinanciere.module.scss";
import {
  ConformiteGarantieFinanciereEditFiles,
  FormWithFiles,
} from "./ConformiteGarantieFinanciereEditFiles";
import {
  ConformiteGarantieFinanciereEditFilesNonDiffusable,
  FormWithFileNonDiffusable,
} from "./ConformiteGarantieFinanciereEditFilesNonDiffusable";
import {
  GarantieFinanciereIasFormValues,
  useGarantieFinanciereSchema,
} from "./hooks/useGarantieFinanciereSchema";

const buttonGroupOptionsBoolean = [
  {
    label: "Oui",
    value: true,
  },
  {
    label: "Non",
    value: false,
  },
];

type ConformiteGarantieFinanciereEditFormIasProps = {
  formConfig: ReturnType<
    typeof useConformiteGarantieFinanciereIasEdit
  >["formConfig"];
  onSubmit?: ReturnType<
    typeof useConformiteGarantieFinanciereIasEdit
  >["onSubmit"];
  garantieFinanciere: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | null;
  millesime: number;
  disabled?: boolean;
  siren: string;
};
const getFileNonDiffusableWithoutFile = (
  file: {
    isValid: boolean;
    pathname?: string | undefined;
    filename?: string | undefined;
    file?: File | undefined;
  } | null
): GatewayGarantieFinanciere.ConformiteGarantieFinanciereFileNonDiffusable | null => {
  if (!file) return null;
  const { file: fileData, ...rest } = file;
  return rest;
};

const getHasGarantieFinanciereValue = (
  data: Record<string, unknown>,
  millesime: number
): boolean | null => {
  if (millesime < 2026) return null;
  return "hasGarantieFinanciere" in data
    ? (data.hasGarantieFinanciere as boolean | null)
    : null;
};
export const useConformiteGarantieFinanciereIasEdit = (
  siren: string,
  millesime: number,
  garantieFinanciere: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | null,
  stopEditing: (
    value:
      GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | undefined,
    avancement: GatewayAvancement.AvancementSociete
  ) => void
) => {
  const schema = useGarantieFinanciereSchema(millesime, "ias");
  const getDate = (date: Date | undefined) => {
    if (!date) return new Date();
    return new Date(date);
  };
  const formConfig = useFormConfig({
    schema,
    defaultValues: {
      millesime,
      hasGarantieFinanciere: garantieFinanciere?.hasGarantieFinanciere ?? null,
      hasExpertComptable: garantieFinanciere?.hasExpertComptable ?? false,
      encaissementFonds: garantieFinanciere?.encaissementFonds ?? false,
      couvertParMandat: garantieFinanciere?.couvertParMandat ?? false,
      nonDiffusable: {
        noCommunication: garantieFinanciere?.noCommunication ?? null,
        fileNonDiffusable: garantieFinanciere?.fileNonDiffusable ?? null,
      },
      files:
        garantieFinanciere?.files?.map((file) => ({
          ...file,
          startDate: getDate(file.startDate),
          endDate: getDate(file.endDate),
        })) ?? [],
      infoGarantie: {
        primesEncaisseesDehorsMandat:
          garantieFinanciere?.primesEncaisseesDehorsMandat ?? 0,
        primesEncaisseesMontant:
          garantieFinanciere?.primesEncaisseesMontant ?? 0,
        indemniteSinistreDehorsMandat:
          garantieFinanciere?.indemniteSinistreDehorsMandat ?? 0,
        indemniteSinistreMontant:
          garantieFinanciere?.indemniteSinistreMontant ?? 0,
        montantGarantie: garantieFinanciere?.montantGarantie ?? 0,
        montantFondRoulement: garantieFinanciere?.montantFondRoulement ?? 0,
      },
    },
  });
  const onSubmit = async ({
    files = [],
    nonDiffusable,
    infoGarantie = {
      primesEncaisseesDehorsMandat: 0,
      primesEncaisseesMontant: 0,
      indemniteSinistreDehorsMandat: 0,
      indemniteSinistreMontant: 0,
      montantGarantie: 0,
      montantFondRoulement: 0,
    },
    ...data
  }: GarantieFinanciereIasFormValues) => {
    const filesToSend = chain(files)
      .filter((file) => !!file?.file)
      .map((file) => file?.file)
      .value() as File[];
    const filesWithoutBlob = files.map((fileData) => {
      if (!fileData?.file) return fileData;
      const { file, ...rest } = fileData;
      return rest;
    });
    const payload: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas = {
      millesime: data.millesime,
      hasGarantieFinanciere: getHasGarantieFinanciereValue(data, millesime),
      encaissementFonds: data.encaissementFonds,
      montantFondRoulement: infoGarantie.montantFondRoulement ?? null,
      primesEncaisseesMontant: infoGarantie.primesEncaisseesMontant ?? null,
      indemniteSinistreMontant: infoGarantie.indemniteSinistreMontant ?? null,
      primesEncaisseesDehorsMandat:
        infoGarantie.primesEncaisseesDehorsMandat ?? null,
      indemniteSinistreDehorsMandat:
        infoGarantie.indemniteSinistreDehorsMandat ?? null,
      montantGarantie: infoGarantie.montantGarantie ?? null,
      couvertParMandat: data.couvertParMandat,
      noCommunication: nonDiffusable.noCommunication,
      files: filesWithoutBlob,
      fileNonDiffusable: getFileNonDiffusableWithoutFile(
        nonDiffusable.fileNonDiffusable
      ),
      hasExpertComptable: data.hasExpertComptable,
    };
    try {
      const avancement =
        await GatewayGarantieFinanciereApi.saveGarantieFinanciereIas(
          siren,
          millesime,
          payload,
          filesToSend.concat(
            nonDiffusable.fileNonDiffusable?.file
              ? [nonDiffusable.fileNonDiffusable?.file]
              : []
          )
        );
      stopEditing(payload, avancement);
    } catch (err) {
      handleGatewayError({
        onUnhandled: () => {
          displayErrorToast(
            "Une erreur est survenue lors de la récupération du formulaire"
          );
        },
      })(err);
    }
  };
  return {
    formConfig,
    onSubmit,
  };
};
export function ConformiteGarantieFinanciereEditFormIas({
  formConfig,
  onSubmit = () => Promise.resolve(),
  disabled,
  millesime,
  garantieFinanciere,
  siren,
}: ConformiteGarantieFinanciereEditFormIasProps) {
  const { me } = useMe();

  const [
    encaissementFonds,
    indemniteSinistreDehorsMandat,
    primesEncaisseesDehorsMandat,
    noCommunication,
    montantFondRoulement,
  ] = formConfig.watch([
    "encaissementFonds",
    "infoGarantie.indemniteSinistreDehorsMandat",
    "infoGarantie.primesEncaisseesDehorsMandat",
    "nonDiffusable.noCommunication",
    "infoGarantie.montantFondRoulement",
  ]);

  const hasGarantieFinanciere =
    millesime >= 2026 ? formConfig.watch("hasGarantieFinanciere") : null;

  const montantCalculeGarantieFinanciere = Math.max(
    ((indemniteSinistreDehorsMandat || 0) +
      (primesEncaisseesDehorsMandat || 0)) /
      6 +
      (montantFondRoulement ?? 0),
    115_000
  ).toFixed(2);

  useOnce(() => {
    if (!disabled && garantieFinanciere !== null) {
      formConfig.trigger().catch(console.error);
    }
  });

  const primesEncaissees = formConfig.watch([
    "infoGarantie.primesEncaisseesDehorsMandat",
    "infoGarantie.primesEncaisseesMontant",
    "infoGarantie.indemniteSinistreDehorsMandat",
    "infoGarantie.indemniteSinistreMontant",
  ]);

  useEffect(() => {
    formConfig.trigger("infoGarantie.montantGarantie").catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primesEncaissees, formConfig.trigger]);

  return (
    <Form formConfig={formConfig} onSubmit={onSubmit}>
      <fieldset disabled={disabled} className={styles.fieldset}>
        <FormGrid disableHorizontalGutter>
          {millesime >= 2026 && (
            <FormRow>
              <FormField>
                <InputButtonGroup<
                  GarantieFinanciereIasFormValues,
                  false,
                  boolean
                >
                  label="Disposez-vous d'une garantie financière ?"
                  name="hasGarantieFinanciere"
                  variant="outline-pop"
                  options={buttonGroupOptionsBoolean}
                  disabled={disabled}
                />
              </FormField>
            </FormRow>
          )}
          <FormRow>
            <FormField>
              <InputButtonGroup<GarantieFinanciereIasFormValues, false, boolean>
                label="Avez vous un expert comptable ?"
                name="hasExpertComptable"
                variant="outline-pop"
                options={buttonGroupOptionsBoolean}
                disabled={disabled}
              />
            </FormField>
          </FormRow>
          {(millesime < 2026 || hasGarantieFinanciere === true) && (
            <FormRow>
              <FormField>
                <InputButtonGroup<
                  GarantieFinanciereIasFormValues,
                  false,
                  boolean
                >
                  label="Avez-vous encaissé des fonds lors des douze derniers mois ?"
                  name="encaissementFonds"
                  variant="outline-pop"
                  options={buttonGroupOptionsBoolean}
                  disabled={disabled}
                />
              </FormField>
            </FormRow>
          )}
          {(millesime < 2026 || hasGarantieFinanciere === true) &&
            encaissementFonds && (
              <>
                <FormRow>
                  <FormField>
                    <InputNumber<GarantieFinanciereIasFormValues>
                      name="infoGarantie.montantGarantie"
                      label="Quel est le montant à date de votre garantie financière ?"
                    />
                  </FormField>
                </FormRow>
                <h4 className={styles.subtitle}>
                  Attestation de couverture sur l&apos;année {millesime}
                </h4>
                {me?.entreprisePrincipale.millesimeActuel === 2024 && (
                  <InfoCard className={styles.cardInformationsPJ}>
                    Dans le cadre du renouvellement 2024, si vous ne disposez
                    pas encore de votre attestation pour l&apos;année 2024/2025,
                    nous vous invitons à renseigner et enregistrer les éléments
                    dont vous disposez à ce jour. Vous pourrez compléter cet axe
                    à réception de votre attestation.
                  </InfoCard>
                )}
                <ConformiteGarantieFinanciereEditFiles
                  form={formConfig as unknown as FormWithFiles}
                  disabled={disabled}
                  siren={siren}
                />
                <FormRow className={styles.firstField}>
                  <FormField>
                    <InputButtonGroup<
                      GarantieFinanciereIasFormValues,
                      false,
                      boolean
                    >
                      label="Tous les produits sont-ils bien couverts par la garantie financière ?"
                      name="couvertParMandat"
                      variant="outline-pop"
                      options={buttonGroupOptionsBoolean}
                      disabled={disabled}
                    />
                  </FormField>
                </FormRow>
                {!noCommunication && (
                  <>
                    <h4 className={styles.subtitle}>
                      Primes encaissées en {millesime - 1}
                    </h4>
                    <FormRow>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIasFormValues>
                          name="infoGarantie.primesEncaisseesDehorsMandat"
                          label="En dehors de tout mandat exprès de l’assureur (en €)"
                        />
                      </FormField>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIasFormValues>
                          name="infoGarantie.primesEncaisseesMontant"
                          label="Dans le cadre d’un mandat exprès de l’assureur (en €)"
                        />
                      </FormField>
                    </FormRow>
                    <h4 className={styles.subtitle}>
                      Indemnités sinistres encaissées en {millesime - 1}
                    </h4>
                    <FormRow>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIasFormValues>
                          name="infoGarantie.indemniteSinistreDehorsMandat"
                          label="En dehors de tout mandat exprès de l’assureur (en €)"
                        />
                      </FormField>
                      <FormField size="half">
                        <InputNumber<GarantieFinanciereIasFormValues>
                          name="infoGarantie.indemniteSinistreMontant"
                          label="Dans le cadre d’un mandat exprès de l’assureur (en €)"
                        />
                      </FormField>
                    </FormRow>
                  </>
                )}
                <FormRow>
                  <FormField>
                    <InputCheckbox<GarantieFinanciereIasFormValues>
                      name="nonDiffusable.noCommunication"
                      label="Ne souhaite pas communiquer sur les primes et indemnités encaissées"
                    />
                  </FormField>
                </FormRow>
                {noCommunication && (
                  <>
                    {!disabled && (
                      <InfoCard
                        type="warning"
                        className={styles.noCommunicationWarning}
                      >
                        <p>
                          Attention : Si vous ne souhaitez pas communiquer sur
                          les primes et indemnités encaissées, vous devez
                          fournir une attestation de la part de votre assureur,
                          garantissant que la société est suffisamment assurée.
                          La validation de cette pièce nécessitant un traitement
                          manuel, le délai de réception de l’attestation de
                          conformité s’en trouvera allongé.
                        </p>
                        <p>
                          Votre formulaire ne sera donc pas validé tant que
                          votre pièce justificative n’aura pas été vérifiée
                          manuellement.
                        </p>
                      </InfoCard>
                    )}
                    <ConformiteGarantieFinanciereEditFilesNonDiffusable
                      form={formConfig as unknown as FormWithFileNonDiffusable}
                      disabled={disabled}
                      siren={siren}
                      millesime={millesime}
                    />
                  </>
                )}
                {!noCommunication && (
                  <div className={styles.calculatedContainer}>
                    <p className={styles.calculatedLabel}>
                      Montant calculé de la garantie financière :
                    </p>
                    <p className={styles.calculatedValue}>
                      {montantCalculeGarantieFinanciere} €
                    </p>
                  </div>
                )}
                <FormRow>
                  <FormField>
                    <InputNumber<GarantieFinanciereIasFormValues>
                      name="infoGarantie.montantFondRoulement"
                      label="Montant du fond de roulement (en €)"
                    />
                  </FormField>
                </FormRow>
              </>
            )}
        </FormGrid>
      </fieldset>
    </Form>
  );
}
